import useAPI from "@toothfairy/shared-api/useApi";
import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppUser from "@toothfairy/shared-ui/AppUser";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";
import API_Consumption from "../../../../API/API_Consumption";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import {
  ENABLE_CONSUMPTION_BILLING,
  getBillingPeriodParams,
  getTotalUoiFromCosts,
} from "../../../../Constants/billing";
import PricingCard from "./PricingCard";
import dayjs from "dayjs";

const getOrganizationPlans = () => [
  {
    type: "business",
    pricingModel: ENABLE_CONSUMPTION_BILLING ? "consumption" : "perUoI",
    category: "organization",
    titleKey: "pricing.planTitles.business",
    descriptionKey: "pricing.planDescriptions.business",
    price: 0.01,
    unitKey: "pricing.units.perUoi",
    unitLabelKey: "pricing.units.unitLabel",
    initialSeats: 25000,
    minimumSeats: 25000,
    maxSeats: 500000,
    stepSize: 5000,
    billingCycleKey: "pricing.billingCycle.payAsYouGo",
    pricingLeafs: [
      {
        text: "pricing.features.payAsYouGo",
        bold: true,
      },
      {
        text: "pricing.features.unlimitedAgents",
      },
      {
        text: "pricing.features.knowledgeHubBusiness",
        params: { count: "5,000" },
      },
      {
        text: "pricing.features.liveApiMcp",
      },
      {
        text: "pricing.features.voiceAgents",
      },
      {
        text: "pricing.features.fineTuning",
      },
      {
        text: "pricing.features.browserIntegration",
      },
      {
        text: "pricing.features.admins",
        params: { count: 10 },
      },
      {
        text: "pricing.features.usersAndAdmins",
        params: { count: 500 },
      },
      {
        text: "pricing.features.prioritySupport",
      },
    ],
    ctaLabelKey: "pricing.cta.rechargeAndStart",
    ctaRoute: "https://app.toothfairyai.com",
    currency: "$",
  },
  {
    type: "enterprise",
    pricingModel: "perUoI",
    category: "organization",
    titleKey: "pricing.planTitles.enterprise",
    descriptionKey: "pricing.planDescriptions.enterprise",
    price: 0.01,
    customUoiPrice: true,
    unitKey: "pricing.units.perUoi",
    unitLabelKey: "pricing.units.unitLabel",
    initialSeats: 75000,
    minimumSeats: 75000,
    maxSeats: 1000000,
    stepSize: 7500,
    billingCycleKey: "pricing.billingCycle.monthly",
    pricingLeafs: [
      {
        text: "pricing.features.everythingInBusiness",
        bold: true,
      },
      {
        text: "pricing.features.admins",
        params: { count: 50 },
      },
      {
        text: "pricing.features.users",
        params: { count: "5,000" },
      },
      {
        text: "pricing.features.customUoiBudget",
      },
      {
        text: "pricing.features.secureDbConnections",
      },
      {
        text: "pricing.features.privateBenchmarks",
      },
      {
        text: "pricing.features.customExecutionHooks",
      },
      {
        text: "pricing.features.browserIntegration",
      },
      {
        text: "pricing.features.dedicatedHosting",
      },
      {
        text: "pricing.features.sso",
      },
      {
        text: "pricing.features.teamsSlack",
      },
      {
        text: "pricing.features.customIntegrations",
      },
      {
        text: "pricing.features.hypercare",
      },
    ],
    ctaLabelKey: "pricing.cta.contactUs",
    ctaRoute: "/contact",
    currency: "$",
  },
];

const AppPricingScreen = ({
  onPress,
  selectedSubscriptionType,
  initialSeats = 1,
  onUpdatePlan,
  currentPlan,
  isUpdate = false,
  isUpdateRequired = false,
  onSeatsChange,
  isBillingDisabled = false,
  credit = 0,
  customUoiPrice = null,
  enterpriseMinUoI = null,
  reservedComputeAllocation = false,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const isLargerScreen = width > 1024;

  const organizationPlans = getOrganizationPlans();
  const subscriptions = [...organizationPlans];

  const [selectedPlan, setSelectedPlan] = useState(selectedSubscriptionType);
  const [seatsData, setSeatsData] = useState({});

  // Wizard state - the consumption model is organization-only (Serverless +
  // Enterprise), so we skip the category selection and go straight to plans.
  const getInitialCategory = () => "organization";
  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory);

  const {
    data: stripeCustomerData,
    loading: stripeCustomerAPIInProgress,
    apiRequest: fetchStripeCustomer,
  } = useAPI(API_Consumption.getStripeCustomer);
  const { user, updateUser } = AppUser.useUser();
  const { getActiveWorkspace } = useWorkspaces();
  const [totalUoIConsumed, setTotalUoIConsumed] = useState(0);

  useEffect(() => {
    if (selectedSubscriptionType) {
      setSelectedPlan(selectedSubscriptionType);
    }
  }, [selectedSubscriptionType]);

  useEffect(() => {
    const fetchConsumption = async () => {
      const workspace = getActiveWorkspace();
      if (!workspace?.id || !workspace?.workspaceToken) return;
      try {
        // Same params as the dashboard and workspace cards so every surface
        // shows the identical consumption figure.
        const now = dayjs();
        const response = await API_Consumption.getCosts({
          workspaceId: workspace.id,
          ...(getBillingPeriodParams(workspace, now) || {
            month: now.get("month") + 1,
            year: now.get("year"),
          }),
          token: workspace.workspaceToken,
          includeChildren: true,
          excludePerMethodperDay: true,
        });
        const data = response?.data || response;
        setTotalUoIConsumed(getTotalUoiFromCosts(data));
      } catch (error) {
        console.error("Error fetching consumption for floor:", error);
      }
    };
    if (isUpdate) {
      fetchConsumption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate, getActiveWorkspace()?.id]);

  useEffect(() => {
    const fetchData = async () => {
      const stripeCustomerData = await fetchStripeCustomer();
      // updateUser needs to be defined or imported
      updateUser({
        ...user,
        stripeCustomerData,
      });
    };
    fetchData();
  }, [user?.id]);

  useEffect(() => {
    const initialSeatsData = {};
    subscriptions.forEach((sub) => {
      if (sub.type === currentPlan) {
        const minValue = sub.minimumSeats || sub.initialSeats;
        if (sub.type === "enterprise" && enterpriseMinUoI) {
          initialSeatsData[sub.type] = Math.max(enterpriseMinUoI, minValue);
        } else {
          initialSeatsData[sub.type] = Math.max(initialSeats, minValue);
        }
      } else {
        initialSeatsData[sub.type] = sub.initialSeats;
      }
    });
    setSeatsData(initialSeatsData);
  }, [initialSeats, currentPlan, enterpriseMinUoI]);

  const minSeatsByConsumptionMap = useMemo(() => {
    const map = {};
    subscriptions.forEach((sub) => {
      map[sub.type] = Math.ceil(totalUoIConsumed / (sub.uoiPerUnit || 1));
    });
    return map;
  }, [totalUoIConsumed, subscriptions]);

  const handlePlanSelect = (subscription) => {
    setSelectedPlan(subscription.type);
    if (onPress) {
      onPress(subscription);
    }

    if (onUpdatePlan) {
      const seats = seatsData[subscription.type] || subscription.initialSeats;
      const effectivePrice =
        subscription.type === "enterprise" && customUoiPrice
          ? customUoiPrice
          : subscription.price;
      const totalPrice = effectivePrice * seats;
      const isBillOverCredit = totalPrice > credit;

      onUpdatePlan(subscription.type, seats, isBillOverCredit, effectivePrice);
    }
  };

  const handleSeatsChange = (seats, subscriptionType) => {
    setSeatsData((prev) => ({
      ...prev,
      [subscriptionType]: seats,
    }));

    if (onSeatsChange) {
      onSeatsChange(seats, subscriptionType);
    }
  };

  const CategoryCard = ({
    icon,
    iconColor,
    title,
    subtitle,
    features,
    isSelected,
    onSelect,
  }) => (
    <TouchableOpacity
      onPress={onSelect}
      style={{
        backgroundColor: currentTheme?.theme?.backgroundColor,
        borderRadius: 16,
        borderWidth: 2,
        borderColor: isSelected
          ? iconColor || currentTheme?.theme?.primary
          : currentTheme?.theme?.borderColor || "#E0E0E0",
        padding: 24,
        minWidth: isLargerScreen ? 320 : 280,
        maxWidth: 380,
        alignItems: "center",
        transition: "all 0.2s ease",
      }}
    >
      <AppIcon
        icon={icon}
        color={iconColor || currentTheme?.theme?.primary}
        size={48}
        style={{ marginBottom: 16 }}
      />
      <AppText
        style={{
          fontSize: 22,
          fontWeight: "bold",
          color: currentTheme?.theme?.font_color,
          marginBottom: 8,
        }}
      >
        {title}
      </AppText>
      <AppText
        style={{
          fontSize: 14,
          color:
            currentTheme?.theme?.secondary_font_color ||
            currentTheme?.theme?.font_color,
          textAlign: "center",
          marginBottom: 16,
          lineHeight: 20,
        }}
      >
        {subtitle}
      </AppText>
      <View style={{ alignItems: "flex-start", width: "100%" }}>
        {features.map((feature, index) => (
          <View
            key={index}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <View
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                backgroundColor: iconColor || currentTheme?.theme?.primary,
              }}
            />
            <AppText
              style={{
                fontSize: 13,
                color: currentTheme?.theme?.font_color,
              }}
            >
              {feature}
            </AppText>
          </View>
        ))}
      </View>
    </TouchableOpacity>
  );

  const CategorySelection = () => (
    <View
      style={{
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 20,
        paddingVertical: 40,
        flex: 1,
        minHeight: "100%",
      }}
    >
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <AppText
          style={{
            fontSize: isLargerScreen ? 28 : 24,
            fontWeight: "bold",
            color: currentTheme?.theme?.font_color,
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          {t("pricing.howWillYouUse")}
        </AppText>
        <AppText
          style={{
            fontSize: isLargerScreen ? 16 : 14,
            color:
              currentTheme?.theme?.secondary_font_color ||
              currentTheme?.theme?.font_color,
            textAlign: "center",
            maxWidth: 500,
            marginBottom: 32,
            lineHeight: 22,
          }}
        >
          {t("pricing.selectOption")}
        </AppText>

        <View
          style={{
            flexDirection: isLargerScreen ? "row" : "column",
            gap: 24,
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          <CategoryCard
            icon={AppAssets?.icons?.genericDepartment}
            iconColor={currentTheme?.theme?.orange}
            title={t("pricing.forOrganizations")}
            subtitle={t("pricing.forOrganizationsSubtitle")}
            features={[
              t("pricing.organizationFeatures.uoiPricing"),
              t("pricing.organizationFeatures.payForUsage"),
              t("pricing.organizationFeatures.unlimitedTeam"),
              t("pricing.organizationFeatures.advancedSupport"),
            ]}
            isSelected={selectedCategory === "organization"}
            onSelect={() => setSelectedCategory("organization")}
          />
        </View>

        <View
          style={{
            flexDirection: "row",
            justifyContent: "center",
            marginTop: 24,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {[
            t("pricing.freeFirstMonth"),
            t("pricing.noCardRequired"),
            t("pricing.hundredPercentPrivate"),
          ].map((benefit, index) => (
            <View
              key={index}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
              }}
            >
              <View
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: currentTheme?.theme?.green || "#4CAF50",
                }}
              />
              <AppText
                style={{
                  fontSize: 12,
                  color: currentTheme?.theme?.font_color,
                }}
              >
                {benefit}
              </AppText>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  const PlansHeader = () => (
    <View
      style={{
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 24,
        paddingHorizontal: 20,
      }}
    >
      <TouchableOpacity
        onPress={() => setSelectedCategory(null)}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          paddingVertical: 8,
          paddingHorizontal: 16,
          borderRadius: 20,
          backgroundColor: currentTheme?.theme?.softBackgroundColor,
        }}
      >
        <AppText
          style={{
            fontSize: 14,
            color: currentTheme?.theme?.primary,
          }}
        >
          ← {t("pricing.changeSelection")}
        </AppText>
      </TouchableOpacity>
      <AppText
        style={{
          fontSize: isLargerScreen ? 24 : 20,
          fontWeight: "bold",
          color: currentTheme?.theme?.font_color,
          marginBottom: 8,
          textAlign: "center",
        }}
      >
        {t("pricing.plansForOrganizations")}
      </AppText>
      <AppText
        style={{
          fontSize: 14,
          color:
            currentTheme?.theme?.secondary_font_color ||
            currentTheme?.theme?.font_color,
          textAlign: "center",
          maxWidth: 500,
        }}
      >
        {t("pricing.organizationPlansSubtitle")}
      </AppText>
    </View>
  );

  const renderSelectedPlans = () => {
    // When editing an existing non-enterprise workspace, the only available
    // plan change is an upgrade to Enterprise — so show ONLY the Enterprise
    // card (what they'd unlock). The Enterprise CTA keeps driving the existing
    // contact-us (email) flow, which is unchanged.
    const isEnterpriseUpgradeOnly =
      isUpdate && currentPlan && currentPlan !== "enterprise";
    const plans = isEnterpriseUpgradeOnly
      ? organizationPlans.filter((p) => p.type === "enterprise")
      : organizationPlans;
    return (
      <View
        style={{
          flexDirection: isLargerScreen ? "row" : "column",
          justifyContent: "center",
          alignItems: isLargerScreen ? "stretch" : "center",
          gap: 16,
          paddingHorizontal: 16,
          paddingTop: 5,
        }}
      >
        {plans.map((subscription) => {
          const currentSeats =
            seatsData[subscription.type] || subscription.initialSeats;
          const isCurrentPlan = subscription.type === currentPlan;

          return (
            <PricingCard
              balance={stripeCustomerData?.balance}
              key={subscription.type}
              subscription={subscription}
              isSelected={selectedPlan === subscription.type}
              onSelect={handlePlanSelect}
              currentSeats={currentSeats}
              onSeatsChange={handleSeatsChange}
              isUpdate={isUpdate}
              disabled={false}
              isCurrentPlan={isCurrentPlan}
              currentPlan={currentPlan}
              customUoiPrice={
                subscription.type === "enterprise" ? customUoiPrice : null
              }
              reservedComputeAllocation={
                subscription.type === "enterprise" ||
                subscription.type === "business"
                  ? reservedComputeAllocation
                  : false
              }
              enterpriseMinUoI={
                subscription.type === "enterprise" ? enterpriseMinUoI : null
              }
              minSeatsByConsumption={
                minSeatsByConsumptionMap[subscription.type] || 0
              }
              totalUoIConsumed={totalUoIConsumed}
            />
          );
        })}
      </View>
    );
  };

  return (
    <ScrollView
      style={{
        backgroundColor: currentTheme?.theme?.backgroundColor,
        flex: 1,
      }}
      contentContainerStyle={{
        paddingVertical: isUpdate ? 16 : 24,
        minHeight: "100%",
      }}
      showsVerticalScrollIndicator={false}
    >
      <View
        style={{
          overflow: "auto",
          marginHorizontal: "auto",
          width: "100%",
        }}
      >
        {!selectedCategory ? (
          <CategorySelection />
        ) : (
          <>
            {!ENABLE_CONSUMPTION_BILLING && <PlansHeader />}
            {isUpdate && currentPlan && currentPlan !== "enterprise" && (
              <View
                style={{
                  alignItems: "center",
                  paddingHorizontal: 20,
                  marginBottom: 16,
                  marginTop: -4,
                }}
              >
                <AppText
                  style={{
                    fontSize: 13,
                    color:
                      currentTheme?.theme?.secondary_font_color ||
                      currentTheme?.theme?.font_color,
                    textAlign: "center",
                    maxWidth: 460,
                    lineHeight: 19,
                  }}
                >
                  {t("pricing.enterpriseUpgradeTagline", {
                    defaultValue:
                      "Step up to Enterprise for high security, compliance and dedicated support — without losing anything you already have. Every Serverless feature stays, plus deeper scale and control.",
                  })}
                </AppText>
              </View>
            )}
            {renderSelectedPlans()}
          </>
        )}

        {!isUpdate && selectedCategory && (
          <View
            style={{
              alignItems: "center",
              marginTop: 32,
              paddingHorizontal: 20,
            }}
          >
            <AppText
              style={{
                fontSize: 12,
                color:
                  currentTheme?.theme?.secondary_font_color ||
                  currentTheme?.theme?.font_color,
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              🧚 {t("pricing.privacyCommitment")} 🔐
            </AppText>
          </View>
        )}
      </View>
    </ScrollView>
  );
};

export default AppPricingScreen;
